import { create } from 'zustand';
import { applyWrite } from '@/shared/lib/store-write';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspaceStore } from '@/modules/workspaces/workspace-store';
import { useNotificationsStore } from '@/modules/notifications/notifications-store';
import { resolveActor } from '@/modules/audit/audit-log';
import type { StakeholderEntityType, StakeholderMatrix, StakeholderAssignments, StakeholderFramework } from '@/modules/stakeholders/types';

const newId = () => crypto.randomUUID();
const matrixKey = (entityType: StakeholderEntityType, entityId: string) => `${entityType}:${entityId}`;

function matrixFromRow(r: any): StakeholderMatrix {
  return {
    id: r.id,
    workspaceId: r.workspace_id,
    entityType: r.entity_type as StakeholderEntityType,
    entityId: r.entity_id,
    frameworkId: r.framework_id ?? 'raci',
    members: Array.isArray(r.members) ? r.members : [],
    assignments: (r.assignments && typeof r.assignments === 'object') ? r.assignments : {},
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

interface StakeholderState {
  matrices: Record<string, StakeholderMatrix>;
  loadingKeys: Record<string, boolean>;

  loadMatrix: (entityType: StakeholderEntityType, entityId: string) => Promise<void>;
  getOrCreateMatrix: (entityType: StakeholderEntityType, entityId: string, workspaceId: string) => Promise<StakeholderMatrix>;
  saveMatrix: (matrix: StakeholderMatrix) => Promise<void>;
  clearWorkspaceAssignments: (workspaceId: string) => Promise<void>;
  purgePerson: (personId: string, workspaceId: string) => void;
  notifyStakeholderAdded: (args: { workspaceId: string; rockId: string; rockTitle: string; memberId: string; assignments?: StakeholderAssignments; milestoneLabels?: Record<string, string>; framework?: StakeholderFramework }) => void;
  notifyStakeholderRemoved: (args: { workspaceId: string; rockId: string; rockTitle: string; memberId: string }) => void;
  notifyLeadAssigned: (args: { workspaceId: string; rockId: string; rockTitle: string; milestoneLabel: string; memberId: string }) => void;
  notifyLeadRemoved: (args: { workspaceId: string; rockId: string; rockTitle: string; milestoneLabel: string; memberId: string; newOwnerName?: string }) => void;
  notifyRoleChanges: (args: {
    workspaceId: string;
    rockId: string;
    rockTitle: string;
    prevAssignments: StakeholderAssignments;
    nextAssignments: StakeholderAssignments;
    milestoneLabels: Record<string, string>;
    framework: StakeholderFramework;
    excludeMemberIds?: Set<string>;
  }) => void;
}

/**
 * Stakeholder-owned server cache. Entries use globally unique entity keys and
 * retain their workspace stamp; each opened entity reloads its canonical row.
 */
export const useStakeholderStore = create<StakeholderState>((set, get) => ({
  matrices: {},
  loadingKeys: {},

  loadMatrix: async (entityType, entityId) => {
    const wsId = useWorkspaceStore.getState().currentWorkspaceId;
    if (!wsId) return;
    const key = matrixKey(entityType, entityId);
    set((s) => ({ loadingKeys: { ...s.loadingKeys, [key]: true } }));
    const { data } = await supabase
      .from('stakeholder_matrices')
      .select('*')
      .eq('workspace_id', wsId)
      .eq('entity_type', entityType)
      .eq('entity_id', entityId)
      .maybeSingle();
    set((s) => ({
      loadingKeys: { ...s.loadingKeys, [key]: false },
      matrices: data
        ? { ...s.matrices, [key]: matrixFromRow(data) }
        : s.matrices,
    }));
  },

  getOrCreateMatrix: async (entityType, entityId, workspaceId) => {
    const key = matrixKey(entityType, entityId);
    const existing = get().matrices[key];
    if (existing) return existing;

    const { data: found } = await supabase
      .from('stakeholder_matrices')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('entity_type', entityType)
      .eq('entity_id', entityId)
      .maybeSingle();

    if (found) {
      const matrix = matrixFromRow(found);
      set((s) => ({ matrices: { ...s.matrices, [key]: matrix } }));
      return matrix;
    }

    const frameworkId = useWorkspaceStore.getState().settings?.stakeholderFrameworkId ?? 'raci';
    const matrix: StakeholderMatrix = {
      id: newId(),
      workspaceId,
      entityType,
      entityId,
      frameworkId,
      members: [],
      assignments: {},
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    const { error } = await supabase.from('stakeholder_matrices').insert({
      id: matrix.id,
      workspace_id: workspaceId,
      entity_type: entityType,
      entity_id: entityId,
      framework_id: frameworkId,
      members: [],
      assignments: {},
    } as any);
    if (!error) {
      set((s) => ({ matrices: { ...s.matrices, [key]: matrix } }));
    }
    return matrix;
  },

  saveMatrix: async (matrix) => {
    const key = matrixKey(matrix.entityType, matrix.entityId);
    const updated = { ...matrix, updatedAt: new Date().toISOString() };
    set((s) => ({ matrices: { ...s.matrices, [key]: updated } }));
    await supabase.from('stakeholder_matrices').upsert({
      id: matrix.id,
      workspace_id: matrix.workspaceId,
      entity_type: matrix.entityType,
      entity_id: matrix.entityId,
      framework_id: matrix.frameworkId,
      members: matrix.members as any,
      assignments: matrix.assignments as any,
      updated_at: updated.updatedAt,
    } as any);
  },

  purgePerson: (personId, workspaceId) => {
    // Update local cache immediately
    set((s) => ({
      matrices: Object.fromEntries(
        Object.entries(s.matrices).map(([key, m]) => {
          if (m.workspaceId !== workspaceId) return [key, m];
          if (!m.members.some((sm) => sm.memberId === personId)) return [key, m];
          const newMembers = m.members.filter((sm) => sm.memberId !== personId);
          const newAssignments: StakeholderAssignments = {};
          for (const [msId, byMember] of Object.entries(m.assignments)) {
            const { [personId]: _, ...rest } = byMember;
            if (Object.keys(rest).length > 0) newAssignments[msId] = rest;
          }
          return [key, { ...m, members: newMembers, assignments: newAssignments }];
        })
      ),
    }));
    // Persist to DB: fetch affected matrices and strip the member
    supabase
      .from('stakeholder_matrices')
      .select('id, members, assignments')
      .eq('workspace_id', workspaceId)
      .then(({ data }) => {
        if (!data) return;
        for (const row of data) {
          const members: Array<{ memberId: string; position: number }> = Array.isArray(row.members) ? row.members as any : [];
          if (!members.some((sm) => sm.memberId === personId)) continue;
          const newMembers = members.filter((sm) => sm.memberId !== personId);
          const assignments = (row.assignments && typeof row.assignments === 'object' ? row.assignments : {}) as StakeholderAssignments;
          const newAssignments: StakeholderAssignments = {};
          for (const [msId, byMember] of Object.entries(assignments)) {
            const { [personId]: _, ...rest } = byMember;
            if (Object.keys(rest).length > 0) newAssignments[msId] = rest;
          }
          // No rollback: this is a best-effort purge across many matrices, and
          // restoring the local cache on one row's failure would resurrect the
          // member in the matrices that were purged successfully. The cache is
          // reloaded per entity by `loadMatrix`, so it self-corrects on open.
          applyWrite(
            supabase
              .from('stakeholder_matrices')
              .update({ members: newMembers as any, assignments: newAssignments as any })
              .eq('id', row.id)
              .select('id'),
            { noun: 'this stakeholder change', context: 'purgePerson' },
          );
        }
      });
  },

  clearWorkspaceAssignments: async (workspaceId) => {
    await supabase
      .from('stakeholder_matrices')
      .update({ assignments: {} } as any)
      .eq('workspace_id', workspaceId);
    set((s) => ({
      matrices: Object.fromEntries(
        Object.entries(s.matrices).map(([k, m]) =>
          m.workspaceId === workspaceId ? [k, { ...m, assignments: {} }] : [k, m]
        )
      ),
    }));
  },

  notifyStakeholderAdded: ({ workspaceId, rockId, rockTitle, memberId, assignments, milestoneLabels, framework }) => {
    const wsMembers = useWorkspaceStore.getState().members;
    const wm = wsMembers.find((m) => m.personId === memberId);
    if (!wm) return;
    resolveActor().then((actor) => {
      if (!actor || actor.userId === wm.userId) return;
      const roleParts: string[] = [];
      for (const [msId, byMember] of Object.entries(assignments ?? {})) {
        const roles = byMember[memberId] ?? [];
        if (roles.length > 0) {
          const names = roles
            .map((l) => framework?.roles.find((r) => r.letter === l)?.name ?? l)
            .join(', ');
          roleParts.push(`${milestoneLabels?.[msId] ?? msId}: ${names}`);
        }
      }
      useNotificationsStore.getState().createNotification({
        workspaceId,
        recipientUserId: wm.userId,
        recipientMemberId: wm.id,
        actorUserId: actor.userId,
        actorName: actor.name ?? undefined,
        type: 'stakeholder_added',
        entityType: 'rock',
        entityId: rockId,
        entityLabel: rockTitle,
        body: roleParts.join('\n'),
        linkData: { rockId },
      });
    });
  },

  notifyStakeholderRemoved: ({ workspaceId, rockId, rockTitle, memberId }) => {
    const wsMembers = useWorkspaceStore.getState().members;
    const wm = wsMembers.find((m) => m.personId === memberId);
    if (!wm) return;
    resolveActor().then((actor) => {
      if (!actor || actor.userId === wm.userId) return;
      useNotificationsStore.getState().createNotification({
        workspaceId,
        recipientUserId: wm.userId,
        recipientMemberId: wm.id,
        actorUserId: actor.userId,
        actorName: actor.name ?? undefined,
        type: 'stakeholder_removed',
        entityType: 'rock',
        entityId: rockId,
        entityLabel: rockTitle,
        body: `${actor.name ?? 'Someone'} removed you as a stakeholder on ${rockTitle}`,
        linkData: { rockId },
      });
    });
  },

  notifyLeadAssigned: ({ workspaceId, rockId, rockTitle, milestoneLabel, memberId }) => {
    const wsMembers = useWorkspaceStore.getState().members;
    const wm = wsMembers.find((m) => m.personId === memberId);
    if (!wm) return;
    resolveActor().then((actor) => {
      if (!actor || actor.userId === wm.userId) return;
      useNotificationsStore.getState().createNotification({
        workspaceId,
        recipientUserId: wm.userId,
        recipientMemberId: wm.id,
        actorUserId: actor.userId,
        actorName: actor.name ?? undefined,
        type: 'stakeholder_lead_assigned',
        entityType: 'rock',
        entityId: rockId,
        entityLabel: rockTitle,
        body: milestoneLabel,
        linkData: { rockId },
      });
    });
  },

  notifyLeadRemoved: ({ workspaceId, rockId, rockTitle, milestoneLabel, memberId, newOwnerName }) => {
    const wsMembers = useWorkspaceStore.getState().members;
    const wm = wsMembers.find((m) => m.personId === memberId);
    if (!wm) return;
    resolveActor().then((actor) => {
      if (!actor || actor.userId === wm.userId) return;
      useNotificationsStore.getState().createNotification({
        workspaceId,
        recipientUserId: wm.userId,
        recipientMemberId: wm.id,
        actorUserId: actor.userId,
        actorName: actor.name ?? undefined,
        type: 'stakeholder_lead_removed',
        entityType: 'rock',
        entityId: rockId,
        entityLabel: rockTitle,
        body: newOwnerName ? `${milestoneLabel} → ${newOwnerName}` : milestoneLabel,
        linkData: { rockId },
      });
    });
  },

  notifyRoleChanges: ({ workspaceId, rockId, rockTitle, prevAssignments, nextAssignments, milestoneLabels, framework, excludeMemberIds }) => {
    const wsMembers = useWorkspaceStore.getState().members;
    resolveActor().then((actor) => {
      if (!actor) return;

      const roleNames = (letters: string[]) =>
        letters.map((l) => framework.roles.find((r) => r.letter === l)?.name ?? l).join(', ');

      const allMemberIds = new Set<string>();
      for (const byMember of Object.values(prevAssignments)) {
        for (const mId of Object.keys(byMember)) allMemberIds.add(mId);
      }
      for (const byMember of Object.values(nextAssignments)) {
        for (const mId of Object.keys(byMember)) allMemberIds.add(mId);
      }

      for (const memberId of allMemberIds) {
        if (excludeMemberIds?.has(memberId)) continue;
        const wm = wsMembers.find((m) => m.personId === memberId);
        if (!wm || wm.userId === actor.userId) continue;

        const changes: Array<{ milestoneId: string; from: string[]; to: string[] }> = [];
        const allMilestoneIds = new Set([
          ...Object.keys(prevAssignments),
          ...Object.keys(nextAssignments),
        ]);
        for (const msId of allMilestoneIds) {
          const from = prevAssignments[msId]?.[memberId] ?? [];
          const to = nextAssignments[msId]?.[memberId] ?? [];
          if (JSON.stringify([...from].sort()) !== JSON.stringify([...to].sort())) {
            changes.push({ milestoneId: msId, from, to });
          }
        }
        if (changes.length === 0) continue;

        let body: string;
        let changeSubtype: string;

        if (changes.length === 1) {
          const { milestoneId, from, to } = changes[0];
          const msLabel = milestoneLabels[milestoneId] ?? milestoneId;
          if (from.length === 0) {
            changeSubtype = 'assign';
            body = `${msLabel}: ${roleNames(to)}`;
          } else if (to.length === 0) {
            changeSubtype = 'unassign';
            body = `${msLabel}: ${roleNames(from)}`;
          } else {
            changeSubtype = 'swap';
            body = `${msLabel}: ${roleNames(to)}`;
          }
        } else {
          changeSubtype = 'multi';
          body = changes
            .map(({ milestoneId, to }) => {
              const msLabel = milestoneLabels[milestoneId] ?? milestoneId;
              return to.length > 0 ? `${msLabel}: ${roleNames(to)}` : `${msLabel}: removed`;
            })
            .join('\n');
        }

        useNotificationsStore.getState().createNotification({
          workspaceId,
          recipientUserId: wm.userId,
          recipientMemberId: wm.id,
          actorUserId: actor.userId,
          actorName: actor.name ?? undefined,
          type: 'stakeholder_role_changed',
          entityType: 'rock',
          entityId: rockId,
          entityLabel: rockTitle,
          body,
          linkData: { rockId, changeSubtype, changeCount: String(changes.length) },
        });
      }
    });
  },
}));
